import { v4 as uuid } from "uuid";
import {
  markScheduleSubmitFailure,
  type ScheduleSubmitKind,
} from "../utils/schedule-submit-error";

/**
 * One request inside a submit attempt. `part` tells apart the requests of one
 * attempt: the post's index in the batch, or the draft id.
 */
export interface ScheduleSubmitScope {
  attemptId: string;
  part: number | string;
}

const openAttempts = new Map<string, string>();
const attemptCovers = new Map<string, Map<string, unknown>>();

/**
 * Returns the unresolved attempt for `context`, or starts a new one. A retry
 * after a failure therefore reuses the attempt, so a submit the server
 * already completed is replayed instead of created twice.
 */
export function beginScheduleAttempt(context: string): string {
  let attemptId = openAttempts.get(context);
  if (!attemptId) {
    attemptId = createScheduleAttemptId();
    openAttempts.set(context, attemptId);
  }
  return attemptId;
}

export function resolveScheduleAttempt(context: string): void {
  const attemptId = openAttempts.get(context);
  if (attemptId) forgetScheduleAttempt(attemptId);
  openAttempts.delete(context);
}

export function resolveScheduleAttempts(contextPrefix: string): void {
  for (const [context, attemptId] of openAttempts) {
    if (!context.startsWith(contextPrefix)) continue;
    forgetScheduleAttempt(attemptId);
    openAttempts.delete(context);
  }
}

export function forgetScheduleAttempt(attemptId: string): void {
  attemptCovers.delete(attemptId);
}

export function createScheduleAttemptId(): string {
  return uuid();
}

export const POST_FORM_ATTEMPT_PREFIX = "post-form:";

export function postFormAttemptContext(postId: string): string {
  return `${POST_FORM_ATTEMPT_PREFIX}${postId}`;
}

function cyrb53(text: string): string {
  let h1 = 0xde_ad_be_ef;
  let h2 = 0x41_c6_ce_57;
  for (let index = 0; index < text.length; index += 1) {
    const char = text.codePointAt(index) ?? 0;
    h1 = Math.imul(h1 ^ char, 2_654_435_761);
    h2 = Math.imul(h2 ^ char, 1_597_334_677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2_246_822_507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3_266_489_909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2_246_822_507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3_266_489_909);
  return (4_294_967_296 * (2_097_151 & h2) + (h1 >>> 0)).toString(36);
}

interface CoverSource {
  videoCoverType?: unknown;
  videoCoverImageTimestamp?: unknown;
  videoCoverPreview?: unknown;
  thumbnailUrl?: unknown;
  url?: unknown;
  mediaFiles?: readonly { url?: unknown }[] | null;
}

/**
 * Uploads a post's video cover once per attempt. Each upload gets a new file
 * name, so a retry that uploaded again would send a different body, miss the
 * Idempotency-Key and schedule the post a second time.
 */
export async function reuseAttemptCover<T>(
  scope: ScheduleSubmitScope,
  post: CoverSource,
  upload: () => Promise<T>
): Promise<T> {
  const key = `${scope.part}:${cyrb53(
    JSON.stringify([
      post.videoCoverType,
      post.videoCoverImageTimestamp,
      post.videoCoverPreview,
      post.thumbnailUrl,
      post.mediaFiles?.[0]?.url,
      post.url,
    ])
  )}`;
  const covers = attemptCovers.get(scope.attemptId);
  if (covers?.has(key)) return covers.get(key) as T;
  const uploaded = await upload();
  const current = attemptCovers.get(scope.attemptId) ?? new Map();
  current.set(key, uploaded);
  attemptCovers.set(scope.attemptId, current);
  return uploaded;
}

/**
 * Sends one schedule submit. With a `scope`, the Idempotency-Key is the
 * attempt, the request's part in it and a hash of the body, so a retry of the
 * same attempt replays what the server already created, while an edited body
 * or a new attempt schedules afresh. Without a scope no key is sent.
 */
export async function withScheduleIdempotencyKey<T>(
  scope: ScheduleSubmitScope | undefined,
  body: unknown,
  send: (idempotencyKey: string | undefined) => Promise<T>,
  kind: ScheduleSubmitKind = "schedule"
): Promise<T> {
  const key = scope
    ? `${scope.attemptId}:${scope.part}:${cyrb53(JSON.stringify(body) ?? "")}`
    : undefined;
  try {
    return await send(key);
  } catch (error) {
    throw markScheduleSubmitFailure(error, error, kind);
  }
}
